import type { ReactNode } from "react";
import { getWhatsAppUrl, trackWhatsAppClick } from "@/lib/whatsapp";

type Variant = "primary" | "whatsapp" | "outline" | "ghost" | "light" | "dark-outline";

const base =
  "inline-flex items-center justify-center gap-2 rounded-md whitespace-nowrap font-medium transition-colors duration-200 cursor-pointer";

const variants: Record<Variant, string> = {
  primary: "bg-primary-800 text-background-50 hover:bg-primary-900",
  whatsapp: "bg-[#128C7E] text-white hover:bg-[#0E6B62]",
  outline: "border border-primary-800 text-primary-800 hover:bg-primary-800 hover:text-background-50",
  ghost: "text-primary-800 hover:text-accent-600 underline-offset-4 hover:underline",
  light: "bg-background-50 text-primary-800 hover:bg-background-100",
  "dark-outline": "border border-background-50/40 text-background-50 hover:bg-background-50/10",
};

const sizes = {
  sm: "px-4 py-2 text-sm",
  md: "px-5 py-3 text-sm",
  lg: "px-7 py-4 text-base",
};

interface WhatsAppLinkProps {
  message: string;
  label?: string;
  sourceSection: string;
  serviceId?: string;
  variant?: Variant;
  icon?: boolean;
  size?: keyof typeof sizes;
  className?: string;
  children?: ReactNode;
  ariaLabel?: string;
}

export default function WhatsAppLink({
  message,
  label,
  sourceSection,
  serviceId,
  variant = "primary",
  icon = true,
  size = "md",
  className = "",
  children,
  ariaLabel,
}: WhatsAppLinkProps) {
  const href = getWhatsAppUrl(message);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      onClick={() =>
        trackWhatsAppClick({
          service_id: serviceId,
          source_section: sourceSection,
          page_path: window.location.pathname,
        })
      }
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {icon && <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>}
      {children || label}
    </a>
  );
}